"use client"

import * as React from "react"
import { ChevronsUpDown, Plus } from "lucide-react"
import Image from "next/image"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar"
import { FEATURE_FLAGS } from "@/lib/config"

export function TeamSwitcher({
  teams,
}: {
  teams: {
    name: string
    logo: React.ElementType | (() => React.ReactNode)
    plan: string
  }[]
}) {
  const { isMobile } = useSidebar()
  const [activeTeam, setActiveTeam] = React.useState(teams[0])

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                {activeTeam.name === "Zipsprout" ? (
                  <img
                    src="/zipsprout-icon.png"
                    alt="Zipsprout"
                    className="size-6 shrink-0 object-contain"
                  />
                ) : typeof activeTeam.logo === "function" && activeTeam.logo.prototype?.render ? (
                  <activeTeam.logo className="size-4" />
                ) : typeof activeTeam.logo === "function" ? (
                  <activeTeam.logo />
                ) : (
                  <activeTeam.logo className="size-4" />
                )}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{activeTeam.name}</span>
                
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">Teams</DropdownMenuLabel>
            {teams.map((team, index) => {
              // RELEASE_HIDDEN: Only Zipsprout is enabled, all others are disabled
              const isZipsprout = team.name === "Zipsprout"
              return (
                <DropdownMenuItem 
                  key={team.name} 
                  onClick={isZipsprout ? () => setActiveTeam(team) : (e) => e.preventDefault()} 
                  className={isZipsprout ? "gap-2 p-2" : "gap-2 p-2 opacity-50 cursor-not-allowed"}
                  disabled={!isZipsprout}
                >
                  <div className="flex size-6 items-center justify-center rounded-sm border">
                    {team.name === "Zipsprout" ? (
                      <img
                        src="/zipsprout-icon.png"
                        alt="Zipsprout"
                        className="size-5 shrink-0 object-contain"
                      />
                    ) : typeof team.logo === "function" && team.logo.prototype?.render ? (
                      <team.logo className="size-4 shrink-0" />
                    ) : typeof team.logo === "function" ? (
                      <team.logo />
                    ) : (
                      <team.logo className="size-4 shrink-0" />
                    )}
                  </div>
                  {team.name}
                  {/* RELEASE_HIDDEN: Removed shortcut indicators from all Teams options */}
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
            {/* RELEASE_HIDDEN: Disabled for production */}
            <DropdownMenuItem disabled className="gap-2 p-2 opacity-50 cursor-not-allowed" onClick={(e) => e.preventDefault()}>
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <Plus className="size-4" />
              </div>
              <div className="font-medium text-muted-foreground">Add team</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
